import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { SITE } from "@/data/site";

const links = [
  { label: "Rooms", href: "#rooms" },
  { label: "Experiences", href: "#experiences" },
  { label: "Food & Bar", href: "#food" },
  { label: "Events", href: "#events" },
  { label: "Location", href: "#location" },
];

export default function Navbar({ onBook }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      data-testid="navbar"
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "py-3" : "py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div
          className={`flex items-center justify-between rounded-full pl-3 pr-2 sm:pl-4 sm:pr-3 py-2 transition-all duration-300 ${
            scrolled ? "glass-strong" : "bg-transparent"
          }`}
        >
          <a href="#top" className="flex items-center" data-testid="navbar-logo">
            <img
              src="https://customer-assets.emergentagent.com/job_gonomikai-launch/artifacts/plhlirsa_Wordmark%20WHT.png"
              alt="GoNomikai"
              className="h-14 sm:h-16 w-auto object-contain"
              style={{ mixBlendMode: "lighten" }}
            />
          </a>

          <nav className="hidden md:flex items-center gap-7">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                className="text-sm text-neutral-300 hover:text-white transition-colors"
                data-testid={`nav-${l.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
              >
                {l.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <button
              data-testid="navbar-book-btn"
              onClick={onBook}
              className="hidden sm:inline-flex items-center gap-2 rounded-full bg-[#62794d] text-[#f5f0ea] px-4 py-2 text-sm font-semibold hover:bg-[#7a9261] active:scale-[0.97] transition"
            >
              Book Stay
            </button>
            <button
              data-testid="navbar-mobile-toggle"
              aria-label="Toggle menu"
              className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-full glass text-white"
              onClick={() => setOpen((o) => !o)}
            >
              {open ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="md:hidden mt-2 mx-4 glass-strong rounded-3xl p-4"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col">
              {links.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="px-3 py-3 text-neutral-200 hover:text-[#A7D38B] border-b border-white/5 last:border-b-0"
                >
                  {l.label}
                </a>
              ))}
              <button
                onClick={() => {
                  setOpen(false);
                  onBook?.();
                }}
                className="mt-3 rounded-full bg-[#62794d] text-[#f5f0ea] py-3 font-semibold"
                data-testid="mobile-menu-book-btn"
              >
                Book Your Stay
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <span className="sr-only">{SITE.name}</span>
    </motion.header>
  );
}
